import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Stack,
  Chip,
  Alert,
  List,
  ListItem,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

const HighlightsTabV2 = ({ highlights = [], onUpdate, onDirtyChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localHighlights, setLocalHighlights] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState(null);

  useEffect(() => {
    setLocalHighlights([...highlights]);
  }, [highlights]);

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isDirty);
    }
  }, [isDirty, onDirtyChange]);

  const handleEdit = () => setIsEditing(true);

  const handleSave = () => {
    onUpdate(localHighlights);
    setIsEditing(false);
    setIsDirty(false);
  };

  const handleCancel = () => {
    setLocalHighlights([...highlights]);
    setIsEditing(false);
    setIsDirty(false);
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleAddNew = () => {
    setEditForm({
      title: '',
      items: []
    });
    setEditingIndex(localHighlights.length);
  };

  const handleEditItem = (index) => {
    setEditForm({ ...localHighlights[index] });
    setEditingIndex(index);
  };

  const handleSaveItem = () => {
    const updated = [...localHighlights];
    if (editingIndex < updated.length) {
      updated[editingIndex] = editForm;
    } else {
      updated.push(editForm);
    }
    setLocalHighlights(updated);
    setEditingIndex(null);
    setEditForm(null);
    setIsDirty(true);
  };

  const handleCancelItem = () => {
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleDeleteItem = (index) => {
    if (window.confirm('Delete this highlight category?')) {
      const updated = localHighlights.filter((_, i) => i !== index);
      setLocalHighlights(updated);
      setIsDirty(true);
    }
  };

  const handleAddHighlightItem = () => {
    setEditForm({
      ...editForm,
      items: [...editForm.items, { text: '', tags: [] }]
    });
  };

  const handleUpdateHighlightItem = (itemIndex, field, value) => {
    const updated = [...editForm.items];
    updated[itemIndex] = { ...updated[itemIndex], [field]: value };
    setEditForm({ ...editForm, items: updated });
  };

  const handleDeleteHighlightItem = (itemIndex) => {
    const updated = editForm.items.filter((_, i) => i !== itemIndex);
    setEditForm({ ...editForm, items: updated });
  };

  const renderItemForm = () => (
    <Card sx={{ mb: 2, bgcolor: 'action.hover' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {editingIndex < localHighlights.length ? 'Edit Highlight Category' : 'Add Highlight Category'}
        </Typography>

        <TextField
          fullWidth
          label="Category Title"
          value={editForm.title}
          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
          placeholder="e.g., Deployed Models, Leadership, Awards"
          sx={{ mb: 3 }}
        />

        <Typography variant="subtitle1" gutterBottom>
          Highlight Items
        </Typography>

        {editForm.items.map((item, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={`Item ${index + 1}`}
                  value={item.text}
                  onChange={(e) => handleUpdateHighlightItem(index, 'text', e.target.value)}
                />
              </Grid>
              <Grid item xs={11}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tags (comma-separated)"
                  value={item.tags?.join(', ') || ''}
                  onChange={(e) => handleUpdateHighlightItem(
                    index,
                    'tags',
                    e.target.value.split(',').map(t => t.trim()).filter(t => t)
                  )}
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton onClick={() => handleDeleteHighlightItem(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddHighlightItem}
          variant="outlined"
          size="small"
        >
          Add Item
        </Button>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleCancelItem} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSaveItem} variant="contained" startIcon={<SaveIcon />}>
            Save Category
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderViewItem = (highlight, index) => (
    <Card key={index} sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">{highlight.title}</Typography>
            <List dense sx={{ mt: 1 }}>
              {highlight.items.map((item, itemIndex) => (
                <ListItem key={itemIndex}>
                  <Typography variant="body2">• {item.text}</Typography>
                </ListItem>
              ))}
            </List>
          </Box>
          {isEditing && (
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => handleEditItem(index)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDeleteItem(index)} size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Career Highlights</Typography>
        {!isEditing ? (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
            Edit
          </Button>
        ) : (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!isDirty}>
              Save Changes
            </Button>
          </Stack>
        )}
      </Box>

      {isDirty && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Click "Save Changes" to commit them.
        </Alert>
      )}

      {isEditing && editingIndex === null && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} sx={{ mb: 2 }}>
          Add Highlight Category
        </Button>
      )}

      {editingIndex !== null && editForm && renderItemForm()}

      {localHighlights.length === 0 ? (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No highlights added yet.
          {isEditing && ' Click "Add Highlight Category" to get started.'}
        </Typography>
      ) : (
        localHighlights.map((highlight, index) =>
          editingIndex === index ? null : renderViewItem(highlight, index)
        )
      )}
    </Box>
  );
};

export default HighlightsTabV2;
